import React, { type ClipboardEvent, forwardRef, type HTMLInputTypeAttribute } from 'react';
import { InputText, type InputTextProps } from 'primereact/inputtext';
import type { KeyFilterType } from 'primereact/keyfilter';
import type { LiliInputBaseProps } from '../../types/LiliInputBaseProps';
import { LiliFloatingLabel } from '../LiliFloatingLabel/LiliFloatingLabel';
import { textInputStyle } from '../../util/styling';
import { useFontClass } from '../../tokens';

export interface LiliTextInputProps
  extends LiliInputBaseProps, Omit<InputTextProps, 'id' | 'onBlur' | 'onFocus' | 'onChange'> {
  value?: string;
  type?: HTMLInputTypeAttribute;
  filterType?: KeyFilterType;
  onChange: (value: string) => void;
  onPaste?: (event: ClipboardEvent<HTMLInputElement>) => void;
  accesoryItem?: React.ReactNode;
  showClear?: boolean;
  isLoading?: boolean;
  onClear?: () => void;
  hint?: string;
}

export const LiliTextInput = forwardRef<HTMLInputElement, LiliTextInputProps>(
  (
    {
      title,
      id,
      value = '',
      errorMessage,
      onBlur,
      onFocus,
      onPaste,
      type = 'text',
      filterType,
      onChange,
      accessoryItemComponent,
      disabled,
      maxLength,
      showClear = true,
      tabIndex,
      isLoading,
      inputMode,
      onClear,
      hint,
      isPublic,
      hideError,
      ...otherProps
    },
    ref
  ) => {
    const { fontClass } = useFontClass();

    const handleClear = () => {
      if (onClear) {
        onClear();
      } else {
        onChange('');
      }
    };

    return (
      <LiliFloatingLabel
        label={title}
        onClear={handleClear}
        errorMessage={errorMessage}
        accessoryItemComponent={accessoryItemComponent}
        showClear={showClear}
        disabled={disabled}
        id={id}
        isLoading={isLoading}
        hint={hint}
        hideError={hideError}>
        {/* Full API: https://primereact.org/textnumber/ */}
        <InputText
          className={textInputStyle(fontClass, value, disabled)}
          placeholder=' '
          value={value}
          onFocus={onFocus}
          onBlur={onBlur}
          onPaste={onPaste}
          onChange={(event) => onChange(event.target.value)}
          id={id}
          type={type}
          keyfilter={filterType}
          disabled={disabled}
          maxLength={maxLength}
          tabIndex={tabIndex}
          ref={ref}
          readOnly={isLoading}
          inputMode={inputMode}
          {...(!isPublic && { 'data-private': 'lipsum' })}
          {...otherProps}
        />
      </LiliFloatingLabel>
    );
  }
);
